import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaProjectDiagram, FaTasks, FaCoins, FaShieldAlt, FaStopwatch, FaSearch, FaBriefcase } from "react-icons/fa";
import { getSiteLinks } from "../../services/apiClient.js";
import FavouriteJobChips from "./FavouriteJobChips.jsx";

// The RDR sections as tinted icon chips (Karen's card-header style). Shared by
// the search-page band and the RdrPage section nav so the colour → category
// mapping stays identical everywhere. Predictive + Completions deliberately
// aren't chips — they live in the header Reports dropdown (UserMenu.jsx).
export const RDR_SECTIONS = [
  // "PCO Dashboard" (Karen's feedback doc, Robert agreed 2026-07-15; was
  // "Main" → "Dashboard" on 07-13). Route stays /rdr/main.
  { key: "main", label: "PCO Dashboard", to: "/rdr/main", icon: FaTasks, tint: "green" },
  { key: "accounts", label: "Accounts", to: "/rdr/accounts", icon: FaCoins, tint: "amber" },
  { key: "health-safety", label: "Health & Safety", to: "/rdr/health-safety", icon: FaShieldAlt, tint: "blue" },
  { key: "workflow", label: "Workflow Management", to: "/rdr/workflow", icon: FaProjectDiagram, tint: "purple" },
  { key: "sla", label: "SLA", to: "/rdr/sla", icon: FaStopwatch, tint: "rose" },
  // Login lands on the Dashboard (2026-07-13), so the search needs a door of
  // its own. Slate = navigation-neutral, not one of the category colours.
  //
  // HIDDEN, not removed (Robert, 13 Aug 2026). It was the odd one out: the other
  // five are report SECTIONS, this one was navigation wearing the same clothes,
  // and on the search page it pointed at the page you were already on. The door
  // it provided is not lost — the header title has always linked to the search
  // (app/InfoPanel.jsx) and now carries a magnifying glass so people can SEE
  // that it does, which is what this chip was really compensating for.
  // Delete `hidden` to bring it back.
  { key: "search", label: "Claim Search", to: "/claims/search", icon: FaSearch, tint: "slate", hidden: true },
];

// The sibling app's address, fetched ONCE per page load and shared by every
// chip row on it — a module-level promise, because RdrChips renders on several
// pages and none should each pay for the same tiny request. Empty = no button,
// which is the correct state on dev and wherever the pair is not configured.
let siteLinksPromise = null;
const fetchSiteLinks = () => {
  if (!siteLinksPromise) siteLinksPromise = getSiteLinks().catch(() => ({ archive: "", live: "" }));
  return siteLinksPromise;
};

function RdrChips({ active }) {
  const [liveUrl, setLiveUrl] = useState("");
  useEffect(() => {
    let alive = true;
    fetchSiteLinks().then((l) => { if (alive) setLiveUrl(l.live); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="d-flex align-items-center column-gap-2 flex-wrap no-print">
      {RDR_SECTIONS.filter((s) => !s.hidden).map(({ key, label, to, icon: Icon, tint }) => (
        <Link
          key={key}
          to={to}
          aria-current={key === active ? "page" : undefined}
          className={`btn rdr-chip rdr-chip-${tint}${key === active ? " rdr-chip-active" : ""}`}
        >
          <Icon size={12} aria-hidden="true" />
          {label}
        </Link>
      ))}
      {/* BACK TO LIVE (Robert 2026-08-21) — the mirror of live's grey Archive
          button, and deliberately NOT a new tab: leaving the archive open is
          the whole point of going TO the archive, but coming back is a
          return, not a second window. Same slate grey, because live is a
          different system from here just as this one is from there. */}
      {liveUrl ? (
        <a
          href={liveUrl}
          className="btn rdr-chip rdr-chip-slate"
          data-hint="Current work — jobs from #71511 — in ClaimsDesk Live"
        >
          <FaBriefcase size={12} />
          Live
        </a>
      ) : null}
      {/* Pinned jobs ride the spare room after Live (Robert 2026-09-02, from
          main) — rendered by the band itself so every page with the band gets them. */}
      <FavouriteJobChips />
    </div>
  );
}

export default RdrChips;
